import { createContext, useContext, useState, useEffect } from 'react';
import useBillingInvoices from '../hooks/useBillingInvoices';
import useBillingSubscriptions from '../hooks/useBillingSubscriptions';
import { UserContext } from './UserContext';

export const BillingContext = createContext({});

// Default context values
const defaultContextValue = {
  actions: {
    setChosenPlan: () => {},
    setChosenSchedule: () => {},
    setAdminSeats: () => {},
  },
  state: {
    chosenPlan: 'starter',
    chosenSchedule: 'month',
    adminSeats: 1,
    planPrice: 0,
    pricePerAdminSeat: 0,
    starterPrice: 0,
    starterPriceMonthly: 0,
    premiumPrice: 0,
    premiumPriceMonthly: 0,
    planDesc: '',
    invoices: null,
    subscriptions: null,
  },
};

// Create a component to determine if we should load the full provider
export const ConditionalBillingContextProvider = ({ children }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [currentPath, setCurrentPath] = useState('');

  useEffect(() => {
    // Function to check if current route needs billing context
    const checkRoute = () => {
      const path = window.location.pathname;
      setCurrentPath(path);

      // Only load for routes that need billing functionality
      // This is an allowed routes approach - ONLY these routes will load the BillingContext
      const billingRoutes = [
        '/dashboard',
        '/billing',
        '/account',
        '/orders',
        '/settings',
        // Add any other routes that need the BillingContext
      ];

      // Check if current route starts with any of the billingRoutes
      const needsBilling = billingRoutes.some((route) => path === route || path.startsWith(`${route}`) || path === '/');

      setShouldLoad(needsBilling);
    };

    // Check initially
    checkRoute();

    // Listen for route changes
    const handleRouteChange = () => {
      checkRoute();
    };

    window.addEventListener('popstate', handleRouteChange);

    // Also listen for React Router changes by periodically checking the URL
    // This is needed because popstate doesn't fire with programmatic navigation
    const interval = setInterval(() => {
      if (window.location.pathname !== currentPath) {
        checkRoute();
      }
    }, 300);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      clearInterval(interval);
    };
  }, [currentPath]);

  // Use the full provider for routes that need it, otherwise use empty provider
  return shouldLoad ? (
    <BillingContextProvider>{children}</BillingContextProvider>
  ) : (
    <BillingContext.Provider value={defaultContextValue}>{children}</BillingContext.Provider>
  );
};

export const BillingContextProvider = ({ children, props }) => {
  const {
    state: { user_id },
  } = useContext(UserContext);

  const [chosenPlan, setChosenPlan] = useState('starter');
  const [chosenSchedule, setChosenSchedule] = useState('month');
  const [adminSeats, setAdminSeats] = useState(1);
  const { data: invoices } = useBillingInvoices(user_id);
  const { data: subscriptions } = useBillingSubscriptions(user_id);

  //product prod_HSD8bJyBWvHNEW
  const starterPrice = 444; // price_1GtIigLntNDmSZbUCixx54i5
  const starterPriceMonthly = 49; // price_1GtIigLntNDmSZbUNh5HlSbE

  // product prod_HWj7MqMPHV7uLB
  const premiumPrice = 900; // price_1GxffDLntNDmSZbUdsuFwECm
  const premiumPriceMonthly = 99; // price_1GxffCLntNDmSZbU0kmtK5sI

  const planPrice = 0; //useMemo(() => chosenPlan === 'starter' ? chosenSchedule == 'year' ? starterPrice : starterPriceMonthly : chosenPlan === 'enterprise' ? 'TBD' : chosenSchedule == 'year' ? premiumPrice : premiumPriceMonthly,[chosenPlan, chosenSchedule]);
  const pricePerAdminSeat = 19;

  const planDesc = 0; //useMemo(()=>{
  //   return chosenPlan === 'starter' ? 'Assessment, goal setting, up to 100 patients' : chosenPlan === 'premium' ? 'All of Starter, plus expanded branding, 250 patients per provider' : 'All of Premium, API, Custom Dev'
  // },[chosenPlan]);

  return (
    <BillingContext.Provider
      value={{
        actions: {
          setChosenPlan,
          setChosenSchedule,
          setAdminSeats,
        },
        state: {
          chosenPlan,
          chosenSchedule,
          adminSeats,
          planPrice,
          pricePerAdminSeat,
          starterPrice,
          starterPriceMonthly,
          premiumPrice,
          premiumPriceMonthly,
          planDesc,
          invoices,
          subscriptions,
        },
      }}
      {...props}
    >
      {children}
    </BillingContext.Provider>
  );
};
